import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import scheduleService from '../services/scheduleService';
import Dialog from '../components/Dialog';
import ScheduleModal from '../components/ScheduleModal';
import { useDialog } from '../hooks/useDialog';
import { config } from '../config/env';
import Pagination from '../components/Pagination';
import './Schedules.css';

/**
 * Schedules Page
 * 
 * Main page for managing scheduled executions
 */
function Schedules() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentWorkspace } = useWorkspace();
    const subscription = currentWorkspace?.subscription || { plan: 'free' };
    const isFreePlan = subscription.plan === 'free';

    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem('rova_limit_schedules')) || 20);
    const [openMenuId, setOpenMenuId] = useState(null);
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    // Filters
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.schedules__menu-wrapper')) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuId]);

    useEffect(() => {
        if (currentWorkspace) {
            loadSchedules();
        }
    }, [currentWorkspace, currentPage, statusFilter, pageSize]);

    const loadSchedules = async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: pageSize
            };

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            const response = await scheduleService.getSchedules(currentWorkspace.id, params);
            setSchedules(response.schedules || []);

            if (response.pagination) {
                setTotalPages(response.pagination.totalPages);
                setTotalCount(response.pagination.total);
            }
        } catch (err) {
            console.error('Failed to load schedules:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSchedule = () => {
        if (isFreePlan) {
            confirm({
                title: 'Pro Feature',
                message: 'Scheduling is only available on Pro plans and above. Please upgrade your workspace to continue.',
                variant: 'primary',
                confirmText: 'View Plans',
                cancelText: 'Maybe Later'
            }).then((confirmed) => {
                if (confirmed) {
                    navigate('/settings/billing');
                }
            });
            return;
        }
        setEditingSchedule(null);
        setShowModal(true);
    };

    const handleEditSchedule = (schedule, e) => {
        e.stopPropagation();
        setOpenMenuId(null);
        setEditingSchedule(schedule);
        setShowModal(true);
    };

    const handleViewSchedule = (scheduleId) => {
        navigate(`/schedules/${scheduleId}`);
    };

    const handleMenuToggle = (scheduleId, e) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === scheduleId ? null : scheduleId);
    };

    const handlePauseResume = async (schedule, e) => {
        e.stopPropagation();
        setOpenMenuId(null);

        try {
            if (schedule.isPaused) {
                await scheduleService.resumeSchedule(schedule.id, currentWorkspace.id);
                await alert({
                    title: 'Success',
                    message: `Schedule "${schedule.name}" resumed`,
                    variant: 'success'
                });
            } else {
                await scheduleService.pauseSchedule(schedule.id, currentWorkspace.id);
                await alert({
                    title: 'Success',
                    message: `Schedule "${schedule.name}" paused`,
                    variant: 'success'
                });
            }
            loadSchedules();
        } catch (err) {
            console.error('Failed to pause/resume schedule:', err);
            await alert({
                title: 'Error',
                message: 'Failed to update schedule. Please try again.',
                variant: 'danger'
            });
        }
    };

    const handleExecuteNow = async (schedule, e) => {
        e.stopPropagation();
        setOpenMenuId(null);

        const confirmed = await confirm({
            title: 'Execute Schedule Now',
            message: `Execute "${schedule.name}" immediately?`,
            confirmText: 'Execute',
            cancelText: 'Cancel',
            variant: 'primary'
        });

        if (!confirmed) return;

        try {
            await scheduleService.executeScheduleNow(schedule.id, currentWorkspace.id);
            await alert({
                title: 'Success',
                message: 'Schedule execution started',
                variant: 'success'
            });
        } catch (err) {
            console.error('Failed to execute schedule:', err);
            await alert({
                title: 'Error',
                message: 'Failed to execute schedule. Please try again.',
                variant: 'danger'
            });
        }
    };

    const handleDuplicate = async (schedule, e) => {
        e.stopPropagation();
        setOpenMenuId(null);

        try {
            await scheduleService.duplicateSchedule(schedule.id, currentWorkspace.id);
            await loadSchedules();
            await alert({
                title: 'Success',
                message: `Successfully created a copy of "${schedule.name}"`,
                variant: 'success'
            });
        } catch (err) {
            console.error('Failed to duplicate schedule:', err);
            await alert({
                title: 'Error',
                message: 'Failed to duplicate schedule. Please try again.',
                variant: 'danger'
            });
        }
    };

    const handleDelete = async (schedule, e) => {
        e.stopPropagation();
        setOpenMenuId(null);

        const confirmed = await confirm({
            title: 'Delete Schedule',
            message: `Are you sure you want to delete "${schedule.name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await scheduleService.deleteSchedule(schedule.id, currentWorkspace.id);
            loadSchedules();
        } catch (err) {
            console.error('Failed to delete schedule:', err);
            await alert({
                title: 'Error',
                message: 'Failed to delete schedule. Please try again.',
                variant: 'danger'
            });
        }
    };

    const formatNextRun = (date) => {
        if (!date) return '-';
        const nextRun = new Date(date);
        const now = new Date();
        const diffMs = nextRun - now;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 0) return 'Overdue';
        if (diffMins < 60) return `in ${diffMins}m`;
        if (diffHours < 24) return `in ${diffHours}h`;
        if (diffDays < 7) return `in ${diffDays}d`;
        return nextRun.toLocaleDateString();
    };

    const getFrequencyLabel = (cronExpression) => {
        // Simple cron expression parser
        if (!cronExpression) return '-';

        const parts = cronExpression.split(' ');
        if (parts.length < 5) return 'Custom';

        const [min, hour, dayOfMonth, month, dayOfWeek] = parts;

        if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
            return 'Daily';
        }
        if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
            return 'Weekly';
        }
        return 'Custom';
    };

    return (
        <div className="schedules">
            <div className="schedules__header">
                <div className="schedules__header-content">
                    <h1>
                        Schedules
                        <span className="title-badge">{totalCount || 0}</span>
                    </h1>
                    <p>Automate test execution with scheduled runs</p>
                </div>
                <button 
                    className={`schedules__create-btn ${isFreePlan ? 'schedules__create-btn--disabled' : ''}`} 
                    onClick={handleCreateSchedule}
                >
                    + Create Schedule
                </button>
            </div>

            {/* Filters */}
            <div className="schedules__filters">
                <div className="schedules__filter-group">
                    <label>Status:</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="schedules__filter-select"
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="schedules__loading">
                    <div className="schedules__spinner"></div>
                    <p>Loading schedules...</p>
                </div>
            ) : schedules.length === 0 ? (
                <div className="schedules__empty">
                    <div className="schedules__empty-icon">⏰</div>
                    {isFreePlan ? (
                        <>
                            <h2>Unlock Scheduling</h2>
                            <p className="schedules__empty-description">
                                Running tests on a schedule is a premium feature. Upgrade to Pro to automate your quality assurance 24/7.
                            </p>
                            <button className="schedules__empty-cta" onClick={() => navigate('/settings/billing')}>
                                View Pro Plans
                            </button>
                        </>
                    ) : (
                        <>
                            <h2>No Schedules Yet</h2>
                            <p className="schedules__empty-description">
                                Create your first schedule to automate test execution at specific times or intervals.
                            </p>
                            <button className="schedules__empty-cta" onClick={handleCreateSchedule}>
                                Create Your First Schedule
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <div className="schedules__table-container">
                        <table className="schedules__table">
                            <thead>
                                <tr>
                                    <th>SCHEDULE NAME</th>
                                    <th>EXECUTION PLAN</th>
                                    <th>FREQUENCY</th>
                                    <th>NEXT RUN</th>
                                    <th>STATUS</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedules.map(schedule => (
                                    <tr
                                        key={schedule.id}
                                        className="schedules__row"
                                    >
                                        <td className="schedules__cell-name">
                                            <Link to={`/schedules/${schedule.id}`} className="schedules__name-link">
                                                <div className="schedules__name">{schedule.name}</div>
                                            </Link>
                                            {schedule.description && (
                                                <div className="schedules__description">{schedule.description}</div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="schedules__target">
                                                <span className="schedules__plan-name">
                                                    {schedule.executionPlan?.name || 'No Plan'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="schedules__cell-frequency">
                                            {getFrequencyLabel(schedule.cronExpression)}
                                        </td>
                                        <td className="schedules__cell-next-run">
                                            {formatNextRun(schedule.nextRunAt)}
                                        </td>
                                        <td>
                                            <span className={`schedules__status-badge schedules__status-badge--${schedule.isPaused ? 'paused' : 'active'}`}>
                                                {schedule.isPaused ? 'Paused' : 'Active'}
                                            </span>
                                        </td>
                                        <td className="schedules__cell-actions">
                                            <div className="schedules__menu-wrapper">
                                                <button
                                                    className="schedules__menu-btn"
                                                    onClick={(e) => handleMenuToggle(schedule.id, e)}
                                                >
                                                    ⋮
                                                </button>
                                                {openMenuId === schedule.id && (
                                                    <div className="schedules__menu-dropdown">
                                                        <Link to={`/schedules/${schedule.id}`} className="schedules__menu-link">
                                                            View
                                                        </Link>
                                                        <button onClick={(e) => handleEditSchedule(schedule, e)}>
                                                            Edit
                                                        </button>
                                                        <button onClick={(e) => handlePauseResume(schedule, e)}>
                                                            {schedule.isPaused ? 'Resume' : 'Pause'}
                                                        </button>
                                                        {config.IS_DEV && (
                                                            <button onClick={(e) => handleExecuteNow(schedule, e)}>
                                                                Execute Now
                                                            </button>
                                                        )}
                                                        <button onClick={(e) => handleDuplicate(schedule, e)}>
                                                            Duplicate
                                                        </button>
                                                        <button onClick={(e) => handleDelete(schedule, e)} className="danger">
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        limit={pageSize}
                        onPageChange={setCurrentPage}
                        onLimitChange={setPageSize}
                        storageKey="rova_limit_schedules"
                    />
                </>
            )}

            <Dialog {...dialogState} onClose={closeDialog} />

            {showModal && (
                <ScheduleModal
                    onClose={() => {
                        setShowModal(false);
                        setEditingSchedule(null);
                    }}
                    initialData={editingSchedule}
                    onComplete={() => {
                        setShowModal(false);
                        setEditingSchedule(null);
                        loadSchedules();
                    }}
                />
            )}
        </div>
    );
}

export default Schedules;
